import { ToggleAction } from '../components/forms/Toggle/Toggle.controller';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { PostEditorContext } from '../modules/PostEditor/states/PostEditorProvider';
import { PostEditorGroupDataContext } from '../modules/PostEditor/states/PostEditorGroupDataProvider';
import { SelectedSocialProfileValue } from '../components/socialProfiles/SocialProfileSelectorList/SocialProfileSelectorList.view';
import { mapSelectedProfilesValues } from '../modules/PostEditor/helpers/mapSelectedProfilesValues';
import { Modal } from '../components/containers/Modal';

type CustomizeWarningModalProps =
  | {
      value: true;
      complete: undefined;
    }
  | { value: false; complete: (keepNewVal?: boolean) => void };

export const useCustomizePerProfileWarning = (type: 'content' | 'scheduling') => {
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  const { dispatch, state, profiles: availableProfiles } = useContext(PostEditorContext);
  const { postGroups } = state;
  const postGroup = postGroups[postGroupIndex];
  const { customizeSchedulePerProfile, customizeContentPerProfile, activeCustomizeIndex, profiles } = postGroup;
  const stringifiedProfiles = JSON.stringify(profiles, undefined, '');
  const selectedProfiles = useMemo(() => {
    const parsedProfiles: SelectedSocialProfileValue[] = JSON.parse(stringifiedProfiles);
    return mapSelectedProfilesValues(parsedProfiles, availableProfiles);
  }, [stringifiedProfiles, availableProfiles]);

  let isCustomizing: boolean;
  switch (type) {
    case 'content':
      isCustomizing = customizeContentPerProfile;
      break;
    case 'scheduling':
      isCustomizing = customizeSchedulePerProfile;
      break;
  }

  const customizeIndex = typeof activeCustomizeIndex === 'number' && isCustomizing ? activeCustomizeIndex : undefined;

  const [hideCustomizeWarningModal, setHideCustomizeWarningModal] = useState<CustomizeWarningModalProps>({
    value: true,
    complete: undefined
  });

  const keepItOpen = (complete: (keepNewVal?: boolean) => void) => {
    complete(false);
    setHideCustomizeWarningModal({ value: true, complete: undefined });
  };

  const toggleDispatchAction: ToggleAction = useCallback(
    (_, complete) => {
      complete();
      dispatch({
        type: 'toggleCustomizePerProfile',
        payload: { postGroupIndex, customizeIndex, selectedProfiles, type }
      });
    },
    [dispatch, postGroupIndex, customizeIndex, selectedProfiles, type]
  );

  const toggleAction: ToggleAction = useCallback(
    (_, complete) => {
      if (isCustomizing) {
        setHideCustomizeWarningModal({ value: false, complete });
      } else {
        toggleDispatchAction(_, complete);
      }
    },
    [isCustomizing, toggleDispatchAction]
  );

  useEffect(() => {
    if (!isCustomizing) {
      setHideCustomizeWarningModal({ value: true, complete: undefined });
    }
  }, [isCustomizing]);

  const { value: hideWarning, complete: completeToggle } = hideCustomizeWarningModal;

  const warningModalElement = !hideWarning ? (
    <Modal
      onClose={() => keepItOpen(completeToggle)}
      size={'small'}
      showCloseIcon
      title={'Data Loss Alert'}
      closeOnClickOutside={false}
      closeOnEsc
      primaryButtonAction={() => keepItOpen(completeToggle)}
      primaryButtonTitle={'Dismiss'}
      secondaryButtonAction={() => toggleDispatchAction(false, completeToggle)}
      secondaryButtonTitle={'Proceed with Data Loss'}>
      <p className={'no-b-m'}>
        Proceed with Caution. By turning off the content customization you will lose the latest changes you&apos;ve made
        in Customized per profile mode. The content will be reverted to the last state it was in Shared between profiles
        mode.
      </p>
    </Modal>
  ) : undefined;

  return {
    toggleAction,
    warningModalElement
  };
};
